import { merge } from 'lodash-es';

import { buildTextOverflowConfig } from './build-text-overflow-config.js';
import { populateMissingTooltipProps } from './populate-missing-tooltip-props.js';
import { AnnotationsTooltipProps } from '../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import { DEFAULT_LABELS_WIDTH } from '../../core/components/annotations/constants.js';
import { XAxisProps } from '../components/axis/types/annotations-x-axis-props.js';
import { populateMissingAxisProps } from '../components/axis/utils/populate-missing-axis-props.js';
import { DEFAULT_ANNOTATIONS_CHART_CONFIG } from '../constants.js';
import {
  AnnotationsChartConfigProperties,
  AnnotationsChartConfigRequiredProps,
} from '../types/annotations-chart-config-props.js';
import { AnnotationsChartProps } from '../types/annotations-chart-props.js';

/**
 * Deserializes an AnnotationsChart string config
 *
 * @param serializedConfig - string
 * @returns AnnotationsChartConfigProperties
 */
export function deserializeConfig(
  serializedConfig: string,
): AnnotationsChartConfigProperties {
  try {
    return merge(
      {},
      DEFAULT_ANNOTATIONS_CHART_CONFIG,
      JSON.parse(serializedConfig),
    );
  } catch {
    return DEFAULT_ANNOTATIONS_CHART_CONFIG;
  }
}

/**
 * Checks if the contextConfig is a serialized-string configuration
 * @param contextConfig - context configuration.
 * @returns true if the received contextConfig is a string.
 */
export function isSerializedConfig(
  contextConfig: AnnotationsChartConfigProperties | string,
): contextConfig is string {
  return typeof contextConfig === 'string';
}

/**
 * Build AnnotationsChart configuration.
 *
 * @param props - config from props
 * @param contextConfig - config from the context
 * @param xAxis - config from xAxis slot
 * @param tooltip - config from Tooltip slot
 * @param hasNumericalData - boolean if data contains numeric values
 * @returns AnnotationsChartConfigProperties
 */
export function buildAnnotationsChartConfig(
  props: AnnotationsChartProps,
  contextConfig: AnnotationsChartConfigProperties,
  xAxis: XAxisProps,
  tooltip: AnnotationsTooltipProps,
  hasNumericalData: boolean,
): AnnotationsChartConfigRequiredProps {
  const textOverflowConfig = buildTextOverflowConfig(props, contextConfig);

  return {
    showLabels:
      props.showLabels ??
      contextConfig.showLabels ??
      DEFAULT_ANNOTATIONS_CHART_CONFIG.showLabels,
    labelWidth:
      props.labelWidth ?? contextConfig.labelWidth ?? DEFAULT_LABELS_WIDTH,
    height: props.height ?? contextConfig.height,
    xAxisConfig: populateMissingAxisProps(
      xAxis,
      hasNumericalData,
      contextConfig.xAxisConfig,
    ),
    tooltipConfig: populateMissingTooltipProps(
      tooltip,
      contextConfig.tooltipConfig,
    ),
    textOverflowConfig,
  };
}
